import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Trash2, Settings as SettingsIcon, List } from 'lucide-react';
import API_ENDPOINTS from '../../config/api';
import RSVPFieldManager from './RSVPFieldManager';

const RSVPManagement = () => {
  const [submissions, setSubmissions] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [settings, setSettings] = useState({ deadline_date: '', is_open: true, allow_plus_one: true, allow_meal_preference: true });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('submissions');

  useEffect(() => { fetchSubmissions(); fetchSettings(); fetchCustomFields(); }, []);

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.ADMIN_RSVP);
      if (response.data.success) setSubmissions(response.data.submissions);
    } catch (error) { console.error('Error fetching submissions:', error);
    } finally { setLoading(false); }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.GET_RSVP_SETTINGS);
      if (response.data.success) setSettings(response.data.settings);
    } catch (error) { console.error('Error fetching settings:', error); }
  };

  const fetchCustomFields = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.ADMIN_RSVP_FIELDS);
      if (response.data.success) setCustomFields(response.data.fields || []);
    } catch (error) { console.error('Error fetching custom fields:', error); }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.post(API_ENDPOINTS.ADMIN_RSVP, { settings });
      alert('Settings saved successfully!');
    } catch (error) { alert('Error saving settings'); }
  };

  const handleDeleteSubmission = async (id) => {
    if (window.confirm('Are you sure you want to delete this RSVP?')) {
      try {
        await axios.delete(API_ENDPOINTS.ADMIN_RSVP, { data: { submission_id: id } });
        fetchSubmissions();
      } catch (error) { alert('Error deleting RSVP'); }
    }
  };

  const exportToCSV = () => {
    const headers = [...customFields.map(f => f.field_label), 'Submitted At'];
    
    const csvData = submissions.map(s => {
      let customResponses = {};
      try { customResponses = JSON.parse(s.custom_field_responses || '{}'); } catch(e){}
      
      const customValues = customFields.map(f => {
        let val = customResponses[f.id];
        if (!val) {
            if (f.field_label === 'Full name') val = s.full_name;
            if (f.field_label === 'Email') val = s.email;
            if (f.field_label === 'Mobile number') val = s.phone;
            if (f.field_label === 'Attending') val = s.attendance_days;
            if (f.field_label === 'Number of guests') val = s.number_of_guests;
            if (f.field_label === 'Meal preference') val = s.meal_preference;
            if (f.field_label === 'I am bringing a plus one') val = s.plus_one_allowed ? 'Yes' : 'No';
            if (f.field_label === 'Dietary restrictions or allergies') val = s.dietary_restrictions;
        }
        return val || '';
      });
      
      return [
        ...customValues,
        new Date(s.submitted_at).toLocaleString()
      ].map(val => `"${String(val).replace(/"/g, '""')}"`);
    });
    
    const csvContent = [headers.map(h => `"${h}"`), ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'rsvp_submissions.csv';
    a.click();
  };

  if (loading) return <div className="loading">Loading RSVP data...</div>;

  return (
    <div className="admin-rsvp-container">
      <div className="admin-header">
        <h1>RSVP Management</h1>
        <div className="tabs-header">
          <button 
            className={`tab-btn ${activeTab === 'submissions' ? 'active' : ''}`}
            onClick={() => setActiveTab('submissions')}
          >
            <List size={18} /> Submissions
          </button>
          <button 
            className={`tab-btn ${activeTab === 'fields' ? 'active' : ''}`}
            onClick={() => setActiveTab('fields')}
          >
            <SettingsIcon size={18} /> Customize Fields
          </button>
        </div>

        {activeTab === 'submissions' && (
          <>
            <div className="settings-section"><h2>RSVP Settings</h2><div className="settings-form">
        <div className="form-group"><label>Deadline Date</label><input type="date" value={settings.deadline_date} onChange={(e) => setSettings({...settings, deadline_date: e.target.value})} /></div>
        <div className="form-group"><label><input type="checkbox" checked={settings.is_open} onChange={(e) => setSettings({...settings, is_open: e.target.checked})} /> RSVP Form Open</label></div>
        <button className="btn-primary" onClick={handleSaveSettings}>Save Settings</button>
      </div></div>

      <div className="section-header"><h2>RSVP Submissions ({submissions.length})</h2><button className="btn-secondary" onClick={exportToCSV}>Export to CSV</button></div>

      <div style={{overflowX: 'auto'}}>
      <table className="data-table">
        <thead>
          <tr>
            {customFields.map(f => <th key={f.id}>{f.field_label}</th>)}
            <th>Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {submissions.map(sub => {
            let customResponses = {};
            try { customResponses = JSON.parse(sub.custom_field_responses || '{}'); } catch(e){}
            return (
              <tr key={sub.id}>
                {customFields.map(f => {
                  let val = customResponses[f.id];
                  if (!val) {
                    if (f.field_label === 'Full name') val = sub.full_name;
                    if (f.field_label === 'Email') val = sub.email;
                    if (f.field_label === 'Mobile number') val = sub.phone;
                    if (f.field_label === 'Attending') val = sub.attendance_days;
                    if (f.field_label === 'Number of guests') val = sub.number_of_guests;
                    if (f.field_label === 'Meal preference') val = sub.meal_preference;
                    if (f.field_label === 'I am bringing a plus one') val = sub.plus_one_allowed ? 'Yes' : 'No';
                    if (f.field_label === 'Dietary restrictions or allergies') val = sub.dietary_restrictions;
                  }
                  return <td key={f.id}>{String(val || '-')}</td>;
                })}
                <td>{new Date(sub.submitted_at).toLocaleDateString()}</td>
                <td><button onClick={() => handleDeleteSubmission(sub.id)}><Trash2 size={16} /></button></td>
              </tr>
            )
          })}
        </tbody>
      </table>
      </div>
          </>
        )}

        {activeTab === 'fields' && (
          <RSVPFieldManager />
        )}
      </div>
    </div>
  );
};

export default RSVPManagement;